import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { topicLabel } from '@gogrov/shared'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/store/auth'
import { useEffectiveClient } from '@/hooks/useAuth'
import { usePlan, useRegions, useTopics } from '@/hooks/useConfig'
import { useProfile, initialsOf, useBusiness, kindLabel } from '@/hooks/useProfile'
import { useWatchState } from '@/hooks/useAlerts'
import { useRecordsState } from '@/hooks/useRecords'
import { useCoinCase, useUnseenUnlocks } from '@/hooks/useAchievements'
import { useNewsletterAllowed, useListStats } from '@/hooks/useNewsletter'
import { useNewsletterItems } from '@/hooks/useNewsletterItems'
import { SEED_COSTS, EMMA_FREE_PER_WEEK } from '@/hooks/useSeeds'
import { useSeedView } from '@/components/gg/SeedChip'
import { TopicGlyph } from '@/components/gg/TopicGlyph'
import { EMMA_COLORS } from '@/components/emma/emmaEngine'
import { ROUTES, useIsDesktop, setSeedPanel } from '@/components/gg/chrome'
import type { Voice } from '@/types/database'
import { VoiceProfileCard } from '@/components/voice/VoiceProfileCard'
import { useT } from '@/lib/i18n'

const fmt = (n: number) => Number(n).toLocaleString('en-US')

/** Brian's profile.html: what each position does, in one line. */
const VOICE: Record<Voice, { name: string; line: string }> = {
  newcomer: { name: 'Newcomer', line: 'Asks, claims no authority, ends on a question' },
  reporter: { name: 'Reporter', line: 'Goes by the record, ends on what happens next' },
  insider: { name: 'Insider', line: 'Speaks from your own standing, gives direction' },
}

const CHECK = <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
const CHEV = <svg className="sh-chev" viewBox="0 0 24 24" aria-hidden="true"><path d="M9 6l6 6-6 6" /></svg>
const IC: Record<string, ReactNode> = {
  places: <svg viewBox="0 0 24 24"><path d="M12 21s-6.5-5.6-6.5-11a6.5 6.5 0 0 1 13 0c0 5.4-6.5 11-6.5 11z" /><circle cx="12" cy="10" r="2.3" /></svg>,
  topics: <svg viewBox="0 0 24 24"><path d="M5 7h14M5 12h9M5 17h11" /><circle cx="18" cy="12" r="1.5" /></svg>,
  breaking: <svg viewBox="0 0 24 24"><path d="M6 16V11a6 6 0 0 1 12 0v5l1.5 2h-15z" /><path d="M10 20a2 2 0 0 0 4 0" /></svg>,
  records: <svg viewBox="0 0 24 24"><path d="M7 3h7l4 4v14H7z" /><path d="M14 3v4h4" /><path d="M10 12h5M10 16h5" /></svg>,
  newsletter: <svg viewBox="0 0 24 24"><rect x="3.5" y="5.5" width="17" height="13" rx="2" /><path d="M4 7l8 6 8-6" /></svg>,
  coins: <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="7.5" /><circle cx="12" cy="12" r="4" /></svg>,
  account: <svg viewBox="0 0 24 24"><circle cx="12" cy="8.5" r="3.5" /><path d="M5 20c1.2-3.6 3.8-5.5 7-5.5s5.8 1.9 7 5.5" /></svg>,
  emma: <svg viewBox="0 0 24 24"><path d="M12 3.5c3.6 3.3 5.5 7 5.5 10.2A5.5 5.5 0 0 1 6.5 13.7C6.5 10.5 8.4 6.8 12 3.5z" /></svg>,
  voice: <svg viewBox="0 0 24 24"><rect x="9" y="3.5" width="6" height="11" rx="3" /><path d="M6 11.5a6 6 0 0 0 12 0M12 17.5v3" /></svg>,
}

function Row({ to, ic, n, d, dot }: { to: string; ic: ReactNode; n: string; d: string; dot?: boolean }) {
  return (
    <Link to={to}>
      <span className="sh-ic">{ic}</span>
      <span className="sh-t"><span className="sh-n">{n}</span><span className="sh-d">{d}</span></span>
      {dot && <span className="sh-cdot" aria-label="New" />}
      {CHEV}
    </Link>
  )
}

/**
 * Settings (Brian's profile.html v1, Oct 21; Oct 22: "Settings to me is like
 * account and preferences"). Who the account is, this week's seeds, the plan
 * and what's in it, the voice per topic, then a row per setting with a live
 * line saying how it is set now. The four feed controls are listed here too,
 * under their own name, and open their own screens.
 */
export function SettingsHome() {
  const t = useT()
  const desktop = useIsDesktop()
  const user = useAuthStore((s) => s.user)
  const client = useEffectiveClient()
  const { data: profile } = useProfile()
  const { data: plan } = usePlan()
  const { data: regions } = useRegions()
  const { data: topics } = useTopics()
  const { data: coins } = useCoinCase()
  const unseen = useUnseenUnlocks().length
  const { data: nlAllowed } = useNewsletterAllowed()
  const { data: nlStats } = useListStats(!!nlAllowed)
  const { items: nlItems } = useNewsletterItems()
  const s = useSeedView()
  const { data: business } = useBusiness()
  const { data: watch } = useWatchState()
  const { data: records } = useRecordsState()

  // profile.html: the account's name, then the person behind it and the place
  const name = client?.display_name || profile?.full_name || user?.email || ''
  const sub = profile?.full_name && profile.full_name !== name ? profile.full_name : ''
  const place = client?.city_state ?? ''

  // ---------- this week's seeds (the top bar panel's numbers) ----------
  const left = 100 - s.pct
  const seedCls = s.weekly === 0 && s.week > 0 ? ' is-zero' : left < 10 ? ' is-low' : ''

  // ---------- the plan ----------
  const perks: ReactNode[] = plan ? [
    <><b>{fmt(plan.seeds_week)} seeds a week</b>, about {Math.floor(plan.seeds_week / SEED_COSTS.build_story)} stories. Unused seeds roll over, up to a week’s worth.</>,
    <>Up to <b>{plan.max_regions} places</b> and <b>{plan.max_topics} topics</b></>,
    plan.max_pills ? <><b>{plan.max_pills} keywords</b> to steer your feed</> : null,
    plan.records_budget != null ? <>Public records: <b>{plan.records_budget} sources</b> checked every Monday</> : null,
    plan.alert_budget != null ? <>Breaking news: an alert budget of up to <b>{plan.alert_budget} seeds</b></> : null,
    <>Rerolls, rewrites and Emma included ({plan.emma_free_per_week ?? EMMA_FREE_PER_WEEK} questions a week)</>,
    plan.max_seats > 1 ? <><b>{plan.max_seats} seats</b> for your team</> : <><b>1 seat</b></>,
  ].filter(Boolean) : []

  // ---------- the voice: the position per topic, biggest share first ----------
  const mix = (topics ?? []).slice().sort((a, b) => (b.share ?? 0) - (a.share ?? 0))

  // ---------- the live lines ----------
  const pn = (regions ?? []).map((r) => r.name)
  const placesLine = `${pn.length} of ${plan?.max_regions ?? 0}${pn.length ? ` · ${pn.slice(0, 3).join(', ')}${pn.length > 3 ? ` and ${pn.length - 3} more` : ''}` : ''}`
  const topicsLine = `${mix.length} ${mix.length === 1 ? 'topic' : 'topics'} in your mix`
  const nWatches = watch?.watches.length ?? 0
  const breakingLine = !watch
    ? (plan?.alert_budget != null ? `An alert budget of up to ${plan.alert_budget} seeds` : 'The subjects you watch')
    : watch.paused ? 'Paused · nothing is matched or spent'
    : `${nWatches} ${nWatches === 1 ? 'watch' : 'watches'} · ${watch.bank} of ${watch.cap} seeds in the bank`
  const recordsLine = !records
    ? 'Sources checked every Monday'
    : records.paused ? 'Paused · nothing checked' : `${records.spent} of ${records.cap} sources checked this week`
  const nlCount = nlItems.length
  const issue = nlCount ? `${nlCount} ${nlCount === 1 ? 'story' : 'stories'} in this week’s issue` : ''
  // Brian, Oct 29: GoGrov sends the newsletter to the list kept in Your list
  const sendLine = nlStats ? `Your list: ${nlStats.subscribed.toLocaleString()} ${nlStats.subscribed === 1 ? 'subscriber' : 'subscribers'}` : 'Your list'
  const nlLine = !nlAllowed
    ? 'Included on Grove and Harvest'
    : desktop
      ? [sendLine, issue].filter(Boolean).join(' · ')
      : `${issue ? `${issue} · ` : ''}best on a computer`
  const earned = (coins ?? []).filter((c) => c.unlocked_at).length
  const coinsLine = `${earned} of ${(coins ?? []).length} coins${unseen ? ` · ${unseen} new` : ''}`
  const emmaColor = EMMA_COLORS.find((c) => c.id === (profile?.emma_color ?? 'amber'))?.label ?? 'Your accent colour'
  const emmaLine = `${emmaColor} · ${profile?.emma_skin === 'cowboy' ? 'Cowboy' : 'Seed'}`
  const standing = client?.voice_standing?.trim()

  return (
    <div className="sh">
      <section className="sh-acct">
        <div className="sh-av">
          {profile?.avatar_url ? <img src={profile.avatar_url} alt="" /> : initialsOf(profile?.full_name, user?.email)}
        </div>
        <div className="sh-acct__t">
          <h1>{name}</h1>
          <div className="sh-who">{[sub, place].filter(Boolean).join(' · ')}</div>
          <div className="sh-kind">{plan && <><b>{plan.name} plan</b> · </>}{kindLabel(business)}</div>
        </div>
      </section>
      {business?.account_kind === 'business' && business.business_line && (
        <div className="sh-bline"><b>On every post</b>{business.business_line}</div>
      )}

      <div className="sh-cols">
        <div>
          {/* Complete your voice profile (Brian's profile.html, Nov 3: "n of 18 left") */}
          <div className="sh-sect"><span>{t('settings.voiceProfile')}</span></div>
          <VoiceProfileCard />
          <div className="sh-sect"><span>This week’s seeds</span></div>
          <section className="sh-card sh-seeds">
            <div className="sh-k">Left this week</div>
            <div className={`sh-big${seedCls}`}><b>{fmt(s.weekly)}</b><span>of {fmt(s.week)} seeds</span></div>
            <div className="sh-bar"><i style={{ width: `${left}%` }} /></div>
            <div className="sh-line">
              <b>{left}% left</b> · about {s.weekStories} {s.weekStories === 1 ? 'story' : 'stories'} left · back {s.backWhen}
              {s.rollover > 0 && ` · ${fmt(s.rollover)} rolled over from last week`}
              {s.runsOut && ` · at this pace they run out ${s.runsOut === 'today' || s.runsOut === 'tomorrow' ? s.runsOut : `on ${s.runsOut}`}`}
            </div>
            <div className="sh-split">
              <div><span className="sh-k">Purchased</span><b>{fmt(s.purchased)}</b><small>Never expire. Used after this week’s.</small></div>
              <div><span className="sh-k">From coins</span><b>{fmt(s.granted)}</b><small>Seeds your achievements paid.</small></div>
            </div>
            <button type="button" className="gg-btn sh-btn" data-seed-bank onClick={(e) => { e.stopPropagation(); setSeedPanel(true) }}>What uses seeds</button>
          </section>

          <div className="sh-sect"><span>Your plan</span></div>
          <section className="sh-card sh-plan">
            {plan ? (
              <>
                <div className="sh-plan__hd"><h3>{plan.name}</h3><span className="sh-price">${Math.round(plan.price_monthly / 100)}<small> a month</small></span></div>
                <ul>{perks.map((p, i) => <li key={i}>{CHECK}<span>{p}</span></li>)}</ul>
              </>
            ) : <p className="sh-line">No plan on this account yet.</p>}
          </section>
        </div>

        <div>
          <div className="sh-sect"><span>{t('settings.anglePerTopic')}</span><Link to={ROUTES.topics}>{t('settings.changeInTopics')}</Link></div>
          {standing && (
            <Link className="sh-stand" to={`${ROUTES.settings}?tab=voice`}>
              <span>In your words</span>
              {standing}
            </Link>
          )}
          {mix.length > 0 && (
            <nav className="sh-list" aria-label={t('settings.anglePerTopic')}>
              {mix.map((t) => {
                const v = VOICE[(t.voice as Voice) ?? 'newcomer'] ?? VOICE.newcomer
                return (
                  <Link key={t.topic} to={ROUTES.topics}>
                    <span className="sh-ic"><TopicGlyph topic={t.topic} size={20} /></span>
                    <span className="sh-t"><span className="sh-n">{topicLabel(t.topic)}</span><span className="sh-d">{v.line}</span></span>
                    <span className={`sh-v is-${t.voice ?? 'newcomer'}`}>{v.name}</span>
                  </Link>
                )
              })}
            </nav>
          )}
          <p className="sh-vnote">A new story is written in its topic’s position. You can write another one from the story itself.</p>

          <div className="sh-sect"><span>Feed controls</span></div>
          <nav className="sh-list" aria-label="Feed controls">
            <Row to={ROUTES.places} ic={IC.places} n="Places" d={placesLine} />
            <Row to={ROUTES.topics} ic={IC.topics} n="Topics" d={topicsLine} />
            <Row to={ROUTES.breaking} ic={IC.breaking} n="Breaking news" d={breakingLine} />
            <Row to={ROUTES.recordsSettings} ic={IC.records} n="Public records" d={recordsLine} />
          </nav>

          <div className="sh-sect"><span>More</span></div>
          <nav className="sh-list" aria-label="More">
            <Row
              to={desktop && nlAllowed ? ROUTES.newsletterConnect : ROUTES.newsletter}
              ic={IC.newsletter}
              n={desktop ? 'Newsletter settings' : 'Newsletter'}
              d={nlLine}
            />
            <Row to={ROUTES.achievements} ic={IC.coins} n="Achievements" d={coinsLine} dot={unseen > 0} />
            <Row to={`${ROUTES.settings}?tab=voice`} ic={IC.voice} n="Voice" d={standing ? 'Your standing sentence is set' : 'Your standing sentence is empty'} />
            <Row to={`${ROUTES.settings}?tab=emma`} ic={IC.emma} n="Emma" d={emmaLine} />
            <Row to={`${ROUTES.settings}?tab=account`} ic={IC.account} n="Account" d={[user?.email, 'Profile, password, reminders'].filter(Boolean).join(' · ')} />
          </nav>
          <button type="button" className="sh-signout" onClick={() => { void supabase.auth.signOut() }}>Sign out</button>
        </div>
      </div>
    </div>
  )
}
